import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../services/api';
import { useAuth } from './AuthContext';

const UyariContext = createContext(null);

const KONTROL_MS = 60 * 1000; // 60 saniye

function uyariSesiCal() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [
      { frekans: 660, baslangic: 0,   sure: 0.2 },
      { frekans: 660, baslangic: 0.3, sure: 0.2 },
      { frekans: 880, baslangic: 0.6, sure: 0.35 },
    ].forEach(({ frekans, baslangic, sure }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frekans, ctx.currentTime + baslangic);
      gain.gain.setValueAtTime(0.35, ctx.currentTime + baslangic);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + baslangic + sure);
      osc.start(ctx.currentTime + baslangic);
      osc.stop(ctx.currentTime + baslangic + sure + 0.05);
    });
  } catch (e) {}
}

function UyariPopup({ uyari, onOnayla }) {
  const [sure, setSure] = useState(0);

  useEffect(() => {
    if (!uyari) return;
    const gonderilme = new Date(uyari.gonderilmeSaati);
    const tick = () => setSure(Math.floor((Date.now() - gonderilme) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [uyari]);

  if (!uyari) return null;

  const dakika = Math.floor(sure / 60);
  const saniye = sure % 60;
  const sureTxt = dakika > 0
    ? `${dakika} dk ${saniye} sn önce gönderildi`
    : `${saniye} sn önce gönderildi`;

  return createPortal(
    <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 w-full max-w-xs mx-4 flex flex-col items-center gap-5 animate-[popIn_0.3s_ease-out]">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center text-3xl">⏰</div>
        <div className="text-center">
          <p className="text-lg font-bold text-gray-800">
            {uyari.uyariMesaj || 'Hâlâ çalışıyor musunuz?'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{uyari.rolAdi} görev hatırlatıcısı</p>
          <p className="text-xs text-amber-600 mt-2 font-medium">{sureTxt}</p>
        </div>
        <button
          onClick={onOnayla}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition-colors text-sm"
        >
          Buradayım ✓
        </button>
      </div>
      <style>{`
        @keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>,
    document.body
  );
}

export function UyariProvider({ children }) {
  const { personel } = useAuth();
  const [bekleyenUyari, setBekleyenUyari] = useState(null);
  const [popupAcik,     setPopupAcik]     = useState(false);
  const [yanitlanmayanlar, setYanitlanmayanlar] = useState([]);

  const fetchBekleyen = useCallback(async () => {
    try {
      const data = await apiRequest('uyari/bekleyen');
      if (!data || !data.personelUyariLogId) {
        setBekleyenUyari(null);
        setPopupAcik(false);
        return;
      }
      // Sadece yeni bir uyarı ID'si geldiğinde ses çal
      setBekleyenUyari(prev => {
        if (!prev || prev.personelUyariLogId !== data.personelUyariLogId) {
          uyariSesiCal();
        }
        return data;
      });
      setPopupAcik(true);
    } catch (e) {}
  }, []);

  const fetchYanitlanmayanlar = useCallback(async () => {
    try {
      const data = await apiRequest('uyari/yanitlanmayanlar');
      setYanitlanmayanlar(data || []);
    } catch (e) {}
  }, []);

  const onayla = useCallback(async () => {
    if (!bekleyenUyari) return;
    try {
      await apiRequest(`uyari/${bekleyenUyari.personelUyariLogId}/onayla`, 'POST');
      setBekleyenUyari(null);
      setPopupAcik(false);
    } catch (e) {}
  }, [bekleyenUyari]);

  useEffect(() => {
    fetchBekleyen();
    const id = setInterval(fetchBekleyen, KONTROL_MS);
    return () => clearInterval(id);
  }, [fetchBekleyen]);

  // Yönetici için yanıtlanmayanları da izle
  useEffect(() => {
    const tip = personel?.tip;
    if (tip !== 'SüperYönetici' && tip !== 'Yönetici') return;
    fetchYanitlanmayanlar();
    const id = setInterval(fetchYanitlanmayanlar, KONTROL_MS * 2);
    return () => clearInterval(id);
  }, [fetchYanitlanmayanlar]);

  return (
    <UyariContext.Provider value={{ bekleyenUyari, yanitlanmayanlar, fetchYanitlanmayanlar }}>
      {children}
      <UyariPopup
        uyari={popupAcik ? bekleyenUyari : null}
        onOnayla={onayla}
      />
    </UyariContext.Provider>
  );
}

export function useUyari() {
  const ctx = useContext(UyariContext);
  if (!ctx) throw new Error('useUyari: UyariProvider içinde kullanılmalı');
  return ctx;
}
